import { useEffect, useState } from "react";
import styled from "styled-components";
import { BbotModal, BbotTable, Form, notification } from "top-component-library";
import PhoneInput from "top-component-library/form-inputs/PhoneInput";
import TextInput from "top-component-library/form-inputs/TextInput";
import TableEditDeleteIcons from "top-component-library/TableEditDeleteIcons";
import { editFraudContact, saveFraudContact } from "util/ApiCalls";

const FraudContacts = ({ fraudContacts, isLoading, selectedCustomer, refreshData, ...props }) => {
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [contactsToDelete, setContactsToDelete] = useState([]);
  const [fraudContactForm] = Form.useForm();

  useEffect(() => {
    const fraudInitialValues = {
      firstName: contactToEdit?.first_name || "",
      lastName: contactToEdit?.last_name || "",
      phoneNumber: contactToEdit?.phone || "",
    };
    fraudContactForm.setFieldsValue(fraudInitialValues);
  }, [contactToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    const properties = contactToEdit
      ? {
          first_name: fraudContactForm.getFieldValue("firstName"),
          last_name: fraudContactForm.getFieldValue("lastName"),
          phone: fraudContactForm.getFieldValue("phoneNumber"),
        }
      : fraudContactForm.getFieldsValue();
    if (!contactToEdit) {
      await saveFraudContact(selectedCustomer, properties);
    } else {
      await editFraudContact(contactToEdit.id, false, properties);
    }
    setContactToEdit(null);
    setShowAddEditModal(false);
    refreshData();
    notification.success({ message: "Fraud alert contact saved successfully." });
  };

  const onBulkDelete = (selectedContactIds) => {
    setContactsToDelete(fraudContacts.filter((contact) => selectedContactIds.includes(contact.id)));
  };

  const onDelete = async () => {
    await editFraudContact(
      contactsToDelete.map((contact) => contact.id),
      true
    );
    setContactsToDelete([]);
    refreshData();
    notification.success({
      message:
        contactsToDelete.length > 1
          ? "Fraud alert contacts deleted successfully."
          : "Fraud alert contact deleted successfully.",
    });
  };

  const formattedRows = fraudContacts.map((contact) => {
    return {
      key: contact.id,
      name: contact.first_name + " " + contact.last_name,
      phone: contact.phone,
      actions: (
        <span onClick={(e) => e.stopPropagation()}>
          <TableEditDeleteIcons
            onEdit={() => {
              setShowAddEditModal(true);
              setContactToEdit(contact);
            }}
          />
        </span>
      ),
    };
  });

  const modalContent = (
    <Form form={fraudContactForm} layout="vertical" onFinish={onSave}>
      <TextInput id="first-name" name="firstName" label="First Name" placeholder="First name" required={true} />
      <TextInput id="last-name" name="lastName" label="Last Name" placeholder="Last name" required={true} />
      <PhoneInput
        id="phone-number"
        name="phoneNumber"
        label="Phone Number"
        placeholder="(555)555-5555"
        required={true}
      />
    </Form>
  );

  return (
    <>
      <StyledInfoBox>
        Fraud Alerts are special text alerts that will be sent when we detect that a transaction may be fraudulent.
        <br />
        Here, you can set who gets Fraud Alert notifications.
      </StyledInfoBox>
      <BbotTable
        id={"fraud-contacts"}
        data={formattedRows}
        columns={["Name", "Phone", "Actions"]}
        loading={isLoading}
        onAdd={() => setShowAddEditModal(true)}
        interactive={true}
        allowBulkDeletion={true}
        onBulkDelete={onBulkDelete}
        pagination={fraudContacts.length > 10}
      />
      <BbotModal
        title={contactToEdit ? "Edit fraud alert contact" : "Add fraud alert contact"}
        visible={showAddEditModal}
        onCancel={() => {
          setShowAddEditModal(false);
          setContactToEdit(null);
        }}
        onOk={fraudContactForm.submit}
      >
        {modalContent}
      </BbotModal>
      <BbotModal
        title="Are you sure you'd like to delete these fraud alert contacts?"
        visible={contactsToDelete.length > 0}
        onCancel={() => setContactsToDelete([])}
        okText="Delete"
        okType="danger"
        onOk={onDelete}
      >
        You are about to delete the following fraud alert contact{contactsToDelete.length > 1 ? "s" : ""}:{" "}
        <strong>{contactsToDelete.map((contact) => contact.first_name + " " + contact.last_name).join(", ")}</strong>
      </BbotModal>
    </>
  );
};

const StyledInfoBox = styled.div`
  font-size: 14px;
  color: var(--color-neutral__placeholder);
  margin-bottom: 24px;
`;

export default FraudContacts;
